import * as React from 'react'

import {
  SchemaDataFieldsDocument,
  UnifiedEditionFlowLegalEntitiesDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import {
  UnifiedEditionModal,
  type UnifiedEditionModalResult,
  type LegalEntityInfo,
  type EmployeeInfo,
} from 'modules/universal_importer/components/UnifiedEditionModal'
import type { FieldSelectionField } from 'modules/universal_importer/components/FieldSelectionModal'
import { useGetMetadata } from 'modules/universal_importer/components/NuvoImporter/useGetMetadata'
import {
  buildLegalEntityCountryMap,
  getDistinctLeIds,
} from 'modules/universal_importer/lib/multiLeUtils'

interface UnifiedEditionFlowProps {
  companyId: string
  employees: EmployeeInfo[]
  importType?: string
  onClose: () => void
  onConfirm: (result: UnifiedEditionModalResult) => void
}

export const UnifiedEditionFlow: React.FC<UnifiedEditionFlowProps> = ({
  companyId,
  employees,
  importType = 'employee_edition',
  onClose,
  onConfirm,
}) => {
  const {
    data,
    loading,
    error: leError,
  } = useQuery(UnifiedEditionFlowLegalEntitiesDocument, {
    variables: { companyId },
    fetchPolicy: 'network-only',
  })

  const legalEntities: LegalEntityInfo[] = React.useMemo(
    () =>
      data?.companies.legalEntitiesConnection.nodes.map((le) => ({
        id: le.id,
        legalName: le.legalName,
        country: le.country,
      })) ?? [],
    [data]
  )

  const countryMap = React.useMemo(() => buildLegalEntityCountryMap(legalEntities), [legalEntities])
  const allLeIds = React.useMemo(() => getDistinctLeIds(employees), [employees])
  const employeeIds = React.useMemo(() => employees.map((e) => e.id), [employees])

  const initialMetadata = React.useMemo(
    () => ({
      legal_entity_ids: allLeIds.map(BigInt),
      legal_entity_countries: allLeIds.map((id) => countryMap[id]).filter(Boolean),
      employee_ids: employeeIds.map(BigInt),
      legal_entity_country_map: [JSON.stringify(countryMap)],
    }),
    [allLeIds, countryMap, employeeIds]
  )
  const { metadataInput: initialMetadataInput } = useGetMetadata(initialMetadata)

  const {
    data: schemaData,
    loading: schemaLoading,
    error: schemaError,
  } = useQuery(SchemaDataFieldsDocument, {
    variables: {
      companyId,
      importType,
      metadata: initialMetadataInput,
    },
    fetchPolicy: 'network-only',
    skip: employees.length === 0 || legalEntities.length === 0,
  })

  const fields: FieldSelectionField[] = React.useMemo(
    () =>
      schemaData?.universalImporter.schemaAdapters.map((f) => ({
        key: f.key,
        label: f.label,
        identifier: f.identifier,
        hidden: f.hidden,
        leDependant: f.leDependant,
        category: f.category,
      })) ?? [],
    [schemaData]
  )

  const isLoading = loading || schemaLoading

  // If either query fails, close the flow (caller handles fallback)
  React.useEffect(() => {
    if (leError || schemaError) {
      onClose()
    }
  }, [leError, schemaError, onClose])

  return (
    <UnifiedEditionModal
      isOpen
      onClose={onClose}
      onConfirm={onConfirm}
      legalEntities={legalEntities}
      employees={employees}
      fields={fields}
      loading={isLoading}
    />
  )
}
